import { Fragment } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ArrowRight, ChevronRight } from 'lucide-react';
import { useLanguage } from '../i18n/LanguageContext';
import { copy } from '../i18n/copy';
import type { Mission } from '../data/missions';

interface MissionDetailPanelProps {
  mission: Mission;
}

export default function MissionDetailPanel({ mission }: MissionDetailPanelProps) {
  const { t } = useLanguage();

  return (
    <AnimatePresence mode="wait">
      <motion.div
        key={mission.id}
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -12 }}
        transition={{ duration: 0.3 }}
        className={`mt-8 rounded-3xl border bg-white/[0.04] p-8 backdrop-blur-xl ${mission.borderActive} ${mission.glow}`}
      >
        <h3 className={`text-2xl font-bold ${mission.text}`}>{t(mission.detail.title)}</h3>
        <p className="mt-2 max-w-3xl leading-relaxed text-slate-300">{t(mission.detail.text)}</p>

        <div className="mt-6 flex flex-wrap items-center gap-2">
          {mission.detail.steps.map((step, i) => (
            <Fragment key={step.en}>
              <motion.span
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.08 * i, duration: 0.3 }}
                className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold ring-1 ${mission.chip}`}
              >
                <span className="grid size-5 place-items-center rounded-full bg-white/10 text-[11px] font-bold text-white">
                  {i + 1}
                </span>
                {t(step)}
              </motion.span>
              {i < mission.detail.steps.length - 1 && (
                <ChevronRight className="size-4 shrink-0 text-slate-500 rtl:rotate-180" aria-hidden="true" />
              )}
            </Fragment>
          ))}
        </div>

        <a
          href="/src/client/pages/dashboard.html"
          className={`mt-7 inline-flex items-center gap-2 rounded-xl px-5 py-3 text-sm font-bold transition-all ${mission.button}`}
        >
          {t(copy.openSandbox)}
          <ArrowRight className="size-4 rtl:rotate-180" />
        </a>
      </motion.div>
    </AnimatePresence>
  );
}
